import { useState } from 'react';
import { useToast } from '../../components/ui/Toast';
import { ApiError } from '../../lib/api';
import { siteMutations } from '../../lib/queries';
import type { Site } from '../../lib/types';

/** Abstand zwischen den Rängen — lässt Platz, um später etwas dazwischenzuschieben. */
const STEP = 10;

/**
 * Legt fest, in welcher Reihenfolge die Baustellen im Schichtplan untereinander
 * stehen.
 *
 * Eigene Leiste statt Pfeilen auf den Karten: die Karten brechen mehrspaltig um,
 * „nach oben" hätte dort keine sichtbare Entsprechung. Hier steht die Liste
 * genau so untereinander wie später im Plan.
 */
export function SiteOrderPanel({ sites }: { sites: Site[] }) {
  const toast = useToast();
  const update = siteMutations.useUpdate();
  const [busy, setBusy] = useState(false);

  const ordered = [...sites].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'de'),
  );

  if (ordered.length < 2) return null;

  const move = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= ordered.length || busy) return;

    const next = [...ordered];
    const moved = next[index];
    const displaced = next[target];
    if (!moved || !displaced) return;
    next[index] = displaced;
    next[target] = moved;

    // Durchgehend neu nummerieren: die Vorgabe ist überall 0, ein reiner Tausch
    // der Werte bliebe dann wirkungslos.
    const changed = next
      .map((site, position) => ({ site, sortOrder: position * STEP }))
      .filter((item) => item.site.sortOrder !== item.sortOrder);

    setBusy(true);
    try {
      for (const { site, sortOrder } of changed) {
        const { id, ...rest } = site;
        await update.mutateAsync({ ...rest, sortOrder, id });
      }
    } catch (error) {
      toast.error(
        error instanceof ApiError ? error.message : 'Reihenfolge konnte nicht gespeichert werden.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="site-order card">
      <header>
        <h2>Reihenfolge im Schichtplan</h2>
        <p className="muted">
          Von oben nach unten, so wie die Blöcke im Plan erscheinen. „Büro" und „Bauleiter" gehören
          üblicherweise nach ganz oben.
        </p>
      </header>

      <ol className="site-order-list">
        {ordered.map((site, index) => (
          <li key={site.id} style={{ '--site': site.color } as never}>
            <span className="site-order-rank numeric">{index + 1}</span>
            <span className="site-order-name">{site.name}</span>

            <span className="site-order-buttons">
              <button
                type="button"
                className="btn btn-icon btn-small"
                onClick={() => void move(index, -1)}
                disabled={index === 0 || busy}
                aria-label={`${site.name} nach oben`}
              >
                ↑
              </button>
              <button
                type="button"
                className="btn btn-icon btn-small"
                onClick={() => void move(index, 1)}
                disabled={index === ordered.length - 1 || busy}
                aria-label={`${site.name} nach unten`}
              >
                ↓
              </button>
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}
